import {Card, Choice, DatePicker} from 'platform/components';
import {Grid, GridItem, Show} from 'platform/foundation';

import {isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useReadCodelistQuery} from '@dms/api/core';
import {
  LeasingPrimaryTargetGroup,
  LeasingTargetGroup,
  SaveSaleVehicleLeasingBody,
} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';

import {composePath, getApiDateString, parseDate, useNavigate} from 'shared/web';

import {LeasingTargetGroupField} from './LeasingTargetGroupField';

const FINANCIAL_INSTITUTION_CODELIST_ID = 'leasing_and_credit_company';

const TARGET_GROUPS: LeasingTargetGroup[] = ['PRIVATE', 'COMMERCIAL', 'BOTH'];

const PRIMARY_TARGET_GROUPS: LeasingPrimaryTargetGroup[] = ['PRIVATE', 'COMMERCIAL'];

interface LeasingConfigurationProps {
  vehicleLeasingBody: SaveSaleVehicleLeasingBody;
  financialInstitutionError: string | null;
  isReadOnly: boolean;
  onChange: (vehicleLeasingBody: SaveSaleVehicleLeasingBody) => void;
}

export function LeasingConfiguration(props: LeasingConfigurationProps) {
  const navigate = useNavigate();
  const {
    data: financialInstitutions,
    isLoading: areFinancialInstitutionsLoading,
    isError: areFinancialInstitutionsError,
  } = useReadCodelistQuery({codelistId: FINANCIAL_INSTITUTION_CODELIST_ID});

  const financialInstitutionsSettingsPath = composePath(settingsRoutes.codelistDetail, {
    params: {id: FINANCIAL_INSTITUTION_CODELIST_ID},
    isSearchParamsPreserved: false,
  });

  const updateBody = (changes: Partial<SaveSaleVehicleLeasingBody>) =>
    props.onChange({...props.vehicleLeasingBody, ...changes});

  return (
    <Card
      title={i18n.t('page.vehicle.financingAndLeasing.leasingConfiguration')}
      titleIcon="action/settings"
      variant="inlineGrey"
    >
      <Grid columns={[1, 2, 4]} align="flex-end">
        <GridItem span={1}>
          <Choice
            label={i18n.t('page.vehicle.financingAndLeasing.leasingBank')}
            tooltip={i18n.t('page.vehicle.financingAndLeasing.hints.leasingBank')}
            isRequired
            labelActions={[
              {
                title: i18n.t('general.actions.edit'),
                leftIcon: 'action/settings',
                href: financialInstitutionsSettingsPath,
                onClick: (event) => {
                  event.preventDefault();
                  navigate(financialInstitutionsSettingsPath);
                },
              },
            ]}
            value={props.vehicleLeasingBody.financialInstitutionCodeId ?? null}
            options={(financialInstitutions?.codes ?? [])
              .filter((code) => !code.isDisabled && isNotNilOrEmpty(code.name))
              .map((code) => ({
                value: code.codeId,
                label: code.name,
              }))}
            isLoading={areFinancialInstitutionsLoading}
            isDisabled={props.isReadOnly || areFinancialInstitutionsError}
            errorMessage={
              props.financialInstitutionError ??
              (areFinancialInstitutionsError
                ? i18n.t('general.errors.cannotLoadOptions')
                : undefined)
            }
            onChange={(financialInstitutionCodeId) => updateBody({financialInstitutionCodeId})}
          />
        </GridItem>
        <GridItem span={1}>
          <LeasingTargetGroupField
            label={i18n.t('page.vehicle.financingAndLeasing.targetGroup')}
            tooltip={i18n.t('page.vehicle.financingAndLeasing.hints.targetGroup')}
            value={props.vehicleLeasingBody.targetGroup}
            options={TARGET_GROUPS}
            isDisabled={props.isReadOnly}
            onChange={(targetGroup) =>
              updateBody({
                targetGroup,
                primaryTargetGroup:
                  targetGroup === 'BOTH'
                    ? (props.vehicleLeasingBody.primaryTargetGroup ?? 'PRIVATE')
                    : null,
              })
            }
          />
        </GridItem>
        <GridItem span={1}>
          <DatePicker
            label={i18n.t('entity.accounting.labels.validFrom')}
            tooltip={i18n.t('page.vehicle.financingAndLeasing.hints.leasingValidity')}
            value={parseDate(props.vehicleLeasingBody.validFrom) ?? null}
            isDisabled={props.isReadOnly}
            onChange={(validFrom) =>
              updateBody({validFrom: isNotNil(validFrom) ? getApiDateString(validFrom) : null})
            }
          />
        </GridItem>
        <GridItem span={1}>
          <DatePicker
            label={i18n.t('entity.offerValidityDialog.labels.validTo')}
            tooltip={i18n.t('page.vehicle.financingAndLeasing.hints.leasingValidity')}
            value={parseDate(props.vehicleLeasingBody.validTo) ?? null}
            isDisabled={props.isReadOnly}
            onChange={(validTo) =>
              updateBody({validTo: isNotNil(validTo) ? getApiDateString(validTo) : null})
            }
          />
        </GridItem>
        <Show when={props.vehicleLeasingBody.targetGroup === 'BOTH'}>
          <GridItem span={1}>
            <LeasingTargetGroupField
              label={i18n.t('page.vehicle.financingAndLeasing.primaryTargetGroup')}
              tooltip={i18n.t('page.vehicle.financingAndLeasing.hints.primaryTargetGroup')}
              value={props.vehicleLeasingBody.primaryTargetGroup ?? 'PRIVATE'}
              options={PRIMARY_TARGET_GROUPS}
              isDisabled={props.isReadOnly}
              onChange={(primaryTargetGroup) => updateBody({primaryTargetGroup})}
            />
          </GridItem>
        </Show>
      </Grid>
    </Card>
  );
}
